import { useCallback, useEffect, useState } from 'react';
import type { Project } from '@canvas/shared-types';
import { usePdfStore } from '@/stores/pdfStore';
import { useLayoutMode } from '@/hooks/useLayoutMode';
import {
  loadPdfFromBytes,
  loadPdfFromFile,
  renderThumbnailDataUrl,
} from '@/lib/pdf/pdfDocument';
import {
  bakeAnnotationsIntoPdf,
  countAnnotatedPages,
  downloadBlob,
  exportAnnotatedPdf,
} from '@/lib/pdf/pdfExport';
import { createProjectFromPdf } from '@/lib/pdf/pdfToCanvasProject';
import {
  loadAllPdfPageAnnotations,
  savePdfPageAnnotations,
  uploadPdfDocument,
} from '@/lib/pdf/pdfUpload';
import { idbPutPdfSource } from '@/lib/pdf/pdfSourceIdb';
import { listSignatures } from '@/lib/pdf/signatureStore';
// TODO(subscription): re-enable exportLimits when Pro plans ship
// import { canExportNow, fetchExportQuota, recordExportSuccess } from '@/lib/exportLimits';
import MobileSheet from '@/components/MobileSheet';
import PdfToolbar from './PdfToolbar';
import PdfPageThumbs from './PdfPageThumbs';
import PdfViewport from './PdfViewport';
import PdfToolOptionsPanel from './PdfToolOptionsPanel';
import PdfMobileToolsMenu from './PdfMobileToolsMenu';
import PdfMobileContextStrip from './PdfMobileContextStrip';
import PdfMobilePagesStrip from './PdfMobilePagesStrip';
import PdfSignaturesPanel from './PdfSignaturesPanel';
import CreateSignatureModal from './CreateSignatureModal';
import PasswordProtectModal from './PasswordProtectModal';
import PdfExportMenu from './PdfExportMenu';
import PdfSaveButton from './PdfSaveButton';
import PdfSendEmailModal from './PdfSendEmailModal';
import { sendPdfToEmail, type PdfEmailDraft } from '@/lib/pdf/pdfEmail';
import { PDF_STUDIO_NAME } from '@/data/pdfProduct';

interface PdfEditorViewProps {
  userId: string;
  /** Optional preloaded file from Dashboard picker */
  initialFile?: File | null;
  /** Optional saved doc to reopen */
  initialDocId?: string | null;
  initialBytes?: ArrayBuffer | null;
  initialName?: string | null;
  onClose: () => void;
  onSentToCanvas: (project: Project) => void;
}

export default function PdfEditorView({
  userId,
  initialFile,
  initialBytes,
  initialName,
  initialDocId,
  onClose,
  onSentToCanvas,
}: PdfEditorViewProps) {
  const openSession = usePdfStore((s) => s.openSession);
  const reset = usePdfStore((s) => s.reset);
  const setLoading = usePdfStore((s) => s.setLoading);
  const setError = usePdfStore((s) => s.setError);
  const setBusyAction = usePdfStore((s) => s.setBusyAction);
  const setMeta = usePdfStore((s) => s.setMeta);
  const markSaved = usePdfStore((s) => s.markSaved);
  const name = usePdfStore((s) => s.name);
  const error = usePdfStore((s) => s.error);
  const isLoading = usePdfStore((s) => s.isLoading);
  const busyAction = usePdfStore((s) => s.busyAction);
  const pdfProxy = usePdfStore((s) => s.pdfProxy);
  const pageAnnotations = usePdfStore((s) => s.pageAnnotations);
  const docId = usePdfStore((s) => s.docId);
  const isDirty = usePdfStore((s) => s.isDirty);
  const activePageIndex = usePdfStore((s) => s.activePageIndex);
  const undoPage = usePdfStore((s) => s.undoPage);
  const redoPage = usePdfStore((s) => s.redoPage);
  const setPasswordProtectModalOpen = usePdfStore(
    (s) => s.setPasswordProtectModalOpen
  );
  const setSignatures = usePdfStore((s) => s.setSignatures);
  const setSelectedSignatureId = usePdfStore((s) => s.setSelectedSignatureId);
  const protectionEnabled = usePdfStore((s) => !!s.protection?.enabled);

  const layoutMode = useLayoutMode();
  const isPhone = layoutMode === 'phone';
  const [bootstrapped, setBootstrapped] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [signaturesOpen, setSignaturesOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (initialFile) {
        const loaded = await loadPdfFromFile(initialFile);
        openSession({
          name: initialFile.name,
          sourceBytes: loaded.sourceBytes,
          pdfProxy: loaded.proxy,
        });
      } else if (initialBytes) {
        const loaded = await loadPdfFromBytes(initialBytes);
        let annotations = {};
        if (initialDocId) {
          annotations = await loadAllPdfPageAnnotations(initialDocId);
        }
        openSession({
          name: initialName || 'Document.pdf',
          sourceBytes: loaded.sourceBytes,
          pdfProxy: loaded.proxy,
          docId: initialDocId,
          pageAnnotations: annotations,
        });
      } else {
        setError('No PDF selected');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open PDF');
    } finally {
      setLoading(false);
      setBootstrapped(true);
    }
  }, [
    initialFile,
    initialBytes,
    initialName,
    initialDocId,
    openSession,
    setError,
    setLoading,
  ]);

  useEffect(() => {
    void bootstrap();
    return () => {
      reset();
    };
  }, [bootstrap, reset]);

  // Prefetch signature library so Sign rail is ready on first click
  useEffect(() => {
    if (!userId) return;
    if (usePdfStore.getState().signaturesLoaded) return;
    let alive = true;
    void listSignatures(userId)
      .then((list) => {
        if (!alive) return;
        setSignatures(list);
        if (list.length && !usePdfStore.getState().selectedSignatureId) {
          setSelectedSignatureId(list[0].signatureId);
        }
      })
      .catch((err) => console.warn('Signature prefetch failed', err));
    return () => {
      alive = false;
    };
  }, [userId, setSignatures, setSelectedSignatureId]);

  // Persist annotations for current page when dirty (debounced)
  useEffect(() => {
    if (!docId || !isDirty) return;
    const t = window.setTimeout(() => {
      const json = usePdfStore.getState().pageAnnotations[activePageIndex];
      if (!json) return;
      void savePdfPageAnnotations({
        docId,
        pageIndex: activePageIndex,
        fabricJson: json,
      }).catch((err) => console.warn('Annotation save failed', err));
    }, 1200);
    return () => window.clearTimeout(t);
  }, [docId, isDirty, activePageIndex, pageAnnotations]);

  const persistPdfSession = useCallback(
    async (options?: { uploadSource?: boolean }): Promise<string | null> => {
      const state = usePdfStore.getState();
      if (!state.sourceBytes || !state.pdfProxy) return null;

      const pageAnnotations = state.captureAnnotationsForExport();
      const uploadSource = options?.uploadSource ?? !state.docId;
      let nextDocId = state.docId;

      if (uploadSource) {
        let thumb: string | undefined;
        try {
          thumb = await renderThumbnailDataUrl(state.pdfProxy, 1);
        } catch {
          /* optional */
        }
        const meta = await uploadPdfDocument({
          bytes: state.sourceBytes,
          name: state.name,
          pageCount: state.pdfProxy.numPages,
          thumbnailDataUrl: thumb,
          docId: nextDocId || undefined,
        });
        setMeta(meta);
        nextDocId = meta.docId;
      }

      if (!nextDocId) return null;

      const indices = new Set<number>([
        ...Object.keys(pageAnnotations).map(Number),
        state.activePageIndex,
      ]);
      for (const pageIndex of indices) {
        if (!Number.isFinite(pageIndex)) continue;
        const json = pageAnnotations[pageIndex];
        if (!json) continue;
        await savePdfPageAnnotations({
          docId: nextDocId,
          pageIndex,
          fabricJson: json,
        });
      }

      markSaved();
      return nextDocId;
    },
    [markSaved, setMeta]
  );

  const ensurePersisted = useCallback(async (): Promise<string | null> => {
    const state = usePdfStore.getState();
    if (state.docId && !state.isDirty) return state.docId;
    return persistPdfSession({ uploadSource: !state.docId });
  }, [persistPdfSession]);

  const handleSave = useCallback(async () => {
    const state = usePdfStore.getState();
    if (!state.pdfProxy || !state.sourceBytes) return;

    setBusyAction('Saving…');
    setError(null);
    try {
      await persistPdfSession({ uploadSource: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusyAction(null);
    }
  }, [persistPdfSession, setBusyAction, setError]);

  const handleFinalize = async () => {
    const store = usePdfStore.getState();
    if (!store.pdfProxy || !store.sourceBytes) return;

    setBusyAction('Finalizing all pages…');
    setError(null);
    try {
      const pageAnnotations = store.captureAnnotationsForExport();
      const annotated = countAnnotatedPages(pageAnnotations);
      if (annotated === 0) {
        setError('Nothing to finalize — add annotations on one or more pages first.');
        return;
      }

      const { bytes, stamped } = await bakeAnnotationsIntoPdf({
        sourceBytes: store.sourceBytes,
        pageCount: store.pdfProxy.numPages,
        pageAnnotations,
        // Don't encrypt on finalize — Protect still applies on Download
        protection: null,
      });

      const loaded = await loadPdfFromBytes(bytes);
      usePdfStore.getState().applyFinalizedPdf({
        sourceBytes: loaded.sourceBytes,
        pdfProxy: loaded.proxy,
      });

      if (store.docId) {
        void idbPutPdfSource(store.docId, loaded.sourceBytes).catch(() => undefined);
      }

      setError(
        `Finalized ${stamped} page${stamped === 1 ? '' : 's'} (${Object.values(pageAnnotations).reduce((n, j) => n + ((j?.objects as unknown[] | undefined)?.length ?? 0), 0)} annotations). Ink is now part of the PDF — Download when ready.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Finalize failed');
    } finally {
      setBusyAction(null);
    }
  };

  const exportCurrentPdf = async (): Promise<{ blob: Blob; filename: string } | null> => {
    const store = usePdfStore.getState();
    if (!store.pdfProxy || !store.sourceBytes) return null;

    const pageAnnotations = store.captureAnnotationsForExport();
    const annotated = countAnnotatedPages(pageAnnotations);
    const blob = await exportAnnotatedPdf({
      sourceBytes: store.sourceBytes,
      pdfProxy: store.pdfProxy,
      pageAnnotations,
      filename: store.name,
      protection: store.protection,
    });
    const base = (store.name || 'annotated.pdf').replace(/\.pdf$/i, '');
    const filename =
      annotated > 0 || store.protection?.enabled ? `${base}-edited.pdf` : `${base}.pdf`;
    return { blob, filename };
  };

  const handleDownload = async () => {
    // Free while testing — re-enable quota when subscriptions launch:
    // const quota = await fetchExportQuota();
    // const gate = canExportNow(quota);
    // if (!gate.ok) { setError(gate.reason || 'Export limit reached'); return; }

    setBusyAction('Exporting…');
    setError(null);
    try {
      const exported = await exportCurrentPdf();
      if (!exported) return;
      downloadBlob(exported.blob, exported.filename);
      // await recordExportSuccess();
      void ensurePersisted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBusyAction(null);
    }
  };

  const handleSendEmail = async (draft: PdfEmailDraft) => {
    setBusyAction('Preparing PDF…');
    setError(null);
    try {
      const exported = await exportCurrentPdf();
      if (!exported) {
        setError('Open a PDF before sending.');
        return;
      }
      setBusyAction('Sending…');
      await sendPdfToEmail({
        userId,
        blob: exported.blob,
        filename: exported.filename,
        draft,
      });
      setEmailOpen(false);
      void ensurePersisted();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setEmailOpen(false);
        return;
      }
      setError(err instanceof Error ? err.message : 'Send to email failed');
    } finally {
      setBusyAction(null);
    }
  };

  const handleSendToCanvas = async () => {
    const store = usePdfStore.getState();
    if (!store.pdfProxy) return;

    // Free while testing — re-enable quota when subscriptions launch:
    // const quota = await fetchExportQuota();
    // const gate = canExportNow(quota);
    // if (!gate.ok) { setError(gate.reason || 'Export limit reached'); return; }

    setBusyAction('Importing to canvas…');
    setError(null);
    try {
      const pageAnnotations = store.captureAnnotationsForExport();
      await ensurePersisted();
      const project = await createProjectFromPdf({
        userId,
        name: store.name,
        pdfProxy: store.pdfProxy,
        pageAnnotations,
        includeAnnotations: true,
      });
      // await recordExportSuccess();
      onSentToCanvas(project);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send to canvas failed');
    } finally {
      setBusyAction(null);
    }
  };

  const handleClose = () => {
    if (
      isDirty &&
      !confirm(`Leave ${PDF_STUDIO_NAME}? Unsaved annotation changes may be lost.`)
    ) {
      return;
    }
    onClose();
  };

  const busy = !!busyAction || isLoading;
  const docTitle = name || 'Untitled.pdf';
  const needsSave = isDirty || !docId;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') return;
      if (e.defaultPrevented) return;
      e.preventDefault();
      if (busy) return;
      void handleSave();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, handleSave]);

  return (
    <div
      className={`pdfe-shell${isPhone ? ' pdfe-layout-phone' : ''}`}
      data-layout={layoutMode}
    >
      {isPhone ? (
        <header className="pdfe-topbar pdfe-topbar-phone">
          <button
            type="button"
            className="pdfe-phone-back"
            onClick={handleClose}
            aria-label={`Close ${PDF_STUDIO_NAME}`}
          >
            Back
          </button>
          <div className="pdfe-filename" title={docTitle}>
            {docTitle}
            {isDirty ? ' •' : ''}
          </div>
          <PdfSaveButton
            tone="dark"
            dirty={needsSave}
            busy={busy}
            onSave={() => void handleSave()}
          />
          <PdfExportMenu
            busy={busy}
            tone="dark"
            onDownload={() => void handleDownload()}
            onSendEmail={() => setEmailOpen(true)}
          />
          <button
            type="button"
            className="pdfe-phone-more"
            aria-label="More actions"
            aria-expanded={overflowOpen}
            onClick={() => setOverflowOpen(true)}
          >
            ⋯
          </button>
        </header>
      ) : (
        <header className="pdfe-topbar">
          <div className="pdfe-brand">
            <img
              className="pdfe-brand-mark"
              src="/brand/icon.png"
              alt=""
              width={28}
              height={28}
            />
            <span>{PDF_STUDIO_NAME}</span>
          </div>
          <div className="pdfe-filename" title={docTitle}>
            {docTitle}
            {isDirty ? ' •' : ''}
          </div>
          <button
            type="button"
            className="pdfe-close"
            onClick={handleClose}
            aria-label={`Close ${PDF_STUDIO_NAME}`}
          >
            ×
          </button>
        </header>
      )}

      {isPhone ? (
        <>
          <PdfMobileToolsMenu busy={busy} />
          <PdfMobileContextStrip
            busy={busy}
            onUndo={() => undoPage(activePageIndex)}
            onRedo={() => redoPage(activePageIndex)}
          />
        </>
      ) : (
        <PdfToolbar
          busy={busy}
          dirty={needsSave}
          onSave={() => void handleSave()}
          onDownload={() => void handleDownload()}
          onSendEmail={() => setEmailOpen(true)}
          onSendToCanvas={() => void handleSendToCanvas()}
          onUndo={() => undoPage(activePageIndex)}
          onRedo={() => redoPage(activePageIndex)}
          onProtect={() => setPasswordProtectModalOpen(true)}
          onFinalize={() => void handleFinalize()}
        />
      )}

      {(error || busyAction) && (
        <div className={`pdfe-banner${error ? ' is-error' : ''}`}>
          {busyAction || error}
          {error && (
            <button type="button" className="btn btn-ghost" onClick={() => setError(null)}>
              Dismiss
            </button>
          )}
        </div>
      )}

      <div className="pdfe-body">
        {isLoading || !bootstrapped || !pdfProxy ? (
          <div className="pdfe-loading">Opening PDF…</div>
        ) : isPhone ? (
          <PdfViewport />
        ) : (
          <>
            <PdfPageThumbs />
            <PdfViewport />
            <PdfToolOptionsPanel userId={userId} />
          </>
        )}
      </div>

      {isPhone && bootstrapped && pdfProxy && (
        <>
          <PdfToolOptionsPanel
            userId={userId}
            variant="plate"
            onOpenSignatures={() => setSignaturesOpen(true)}
          />
          <PdfMobilePagesStrip />
        </>
      )}

      <MobileSheet
        open={isPhone && overflowOpen}
        title="Actions"
        onClose={() => setOverflowOpen(false)}
        heightRatio={0.42}
      >
        <div className="pdfe-mobile-overflow">
          <button
            type="button"
            className="pdfe-mobile-overflow-btn"
            disabled={busy}
            onClick={() => {
              setOverflowOpen(false);
              void handleSave();
            }}
          >
            {needsSave ? 'Save' : 'Saved'}
          </button>
          <button
            type="button"
            className="pdfe-mobile-overflow-btn"
            disabled={busy}
            onClick={() => {
              setOverflowOpen(false);
              void handleFinalize();
            }}
          >
            Finalize
          </button>
          <button
            type="button"
            className={`pdfe-mobile-overflow-btn${protectionEnabled ? ' is-protected' : ''}`}
            disabled={busy}
            onClick={() => {
              setOverflowOpen(false);
              setPasswordProtectModalOpen(true);
            }}
          >
            {protectionEnabled ? 'Protected…' : 'Protect'}
          </button>
          <button
            type="button"
            className="pdfe-mobile-overflow-btn"
            disabled={busy}
            onClick={() => {
              setOverflowOpen(false);
              void handleSendToCanvas();
            }}
          >
            Send to canvas
          </button>
        </div>
      </MobileSheet>

      <MobileSheet
        open={isPhone && signaturesOpen}
        title="Signatures"
        onClose={() => setSignaturesOpen(false)}
        heightRatio={0.78}
      >
        <PdfSignaturesPanel userId={userId} />
      </MobileSheet>

      <CreateSignatureModal />
      <PasswordProtectModal />
      <PdfSendEmailModal
        open={emailOpen}
        filename={docTitle}
        busy={busy && emailOpen}
        onClose={() => setEmailOpen(false)}
        onSend={(draft) => void handleSendEmail(draft)}
      />
    </div>
  );
}
